import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, AlertTriangle, Clock, CheckCircle, RefreshCw, Filter } from "lucide-react";
import { groundOpsApi } from "@/api";

const RISK_STYLE = {
  on_time:   { badge: "bg-green-500/15 text-green-400 border-green-500/30",  dot: "bg-green-500"  },
  at_risk:   { badge: "bg-amber-500/15 text-amber-400 border-amber-500/30",  dot: "bg-amber-500 animate-pulse" },
  delayed:   { badge: "bg-red-500/15 text-red-400 border-red-500/30",        dot: "bg-red-500 animate-pulse"   },
  critical:  { badge: "bg-red-600/20 text-red-300 border-red-600/40",        dot: "bg-red-600 animate-pulse"   },
  arriving:  { badge: "bg-sky-500/15 text-sky-400 border-sky-500/30",        dot: "bg-sky-400"    },
  completed: { badge: "bg-slate-500/15 text-slate-400 border-slate-500/30",  dot: "bg-slate-500"  },
};

const STATUS_ICONS = {
  completed:    <CheckCircle size={10} className="text-green-400" />,
  in_progress:  <Clock size={10} className="text-blue-400" />,
  delayed:      <AlertTriangle size={10} className="text-red-400" />,
  blocked:      <AlertTriangle size={10} className="text-purple-400" />,
  not_started:  <span className="w-2 h-2 rounded-full bg-slate-600 inline-block" />,
};

const STATIONS = ["ALL", "DEL", "BOM", "BLR", "HYD", "MAA", "CCU", "AMD", "PNQ"];

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
}
function elapsedStr(arrIso) {
  if (!arrIso) return "";
  const diff = (Date.now() - new Date(arrIso)) / 60000;
  return `${Math.max(0, Math.round(diff))}m on ground`;
}

function FlightCard({ flight, onClick }) {
  const rs = flight.risk_status || "on_time";
  const style = RISK_STYLE[rs] || RISK_STYLE.on_time;
  const pct = Math.round((flight.tasks_completed / Math.max(1, flight.tasks_total)) * 100);

  return (
    <div
      onClick={() => onClick(flight.id)}
      className="bg-[#0e1e31] border border-slate-700/30 hover:border-orange-500/30 rounded-lg p-4 cursor-pointer transition-all hover:bg-[#112035] group"
      data-testid={`flight-card-${flight.flight_number.replace(" ","")}`}
    >
      {/* Row 1: flight + badge */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-black font-mono text-white tracking-tight">{flight.flight_number}</span>
            <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase px-1.5 py-0.5 rounded border ${style.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
              {flight.delay_risk_label || rs.toUpperCase()}
            </span>
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            <span className="font-mono">{flight.origin}</span>
            <span className="mx-1 text-slate-600">→</span>
            <span className="font-mono">{flight.destination}</span>
            <span className="mx-2 text-slate-700">·</span>
            <span className="text-slate-500">{flight.aircraft_reg}</span>
            {flight.stand && <><span className="mx-2 text-slate-700">·</span><span className="text-orange-400/70 font-mono text-[10px]">Stand {flight.stand}</span></>}
          </div>
        </div>
        {flight.delay_risk_score >= 55 && (
          <div className="text-right">
            <div className={`text-xl font-black font-mono ${flight.delay_risk_score >= 65 ? "text-red-400" : "text-amber-400"}`}>{flight.delay_risk_score}</div>
            <div className="text-[8px] text-slate-600 uppercase">risk</div>
          </div>
        )}
      </div>

      {/* Row 2: times */}
      <div className="flex items-center gap-4 text-[10px] mb-3">
        <div>
          <span className="text-slate-600 uppercase tracking-wider">ARR</span>
          <span className="ml-1.5 font-mono text-slate-300">{fmtTime(flight.actual_arrival || flight.scheduled_arrival)}</span>
          {flight.inbound_delay_mins > 0 && (
            <span className="ml-1 text-red-400">+{flight.inbound_delay_mins}m</span>
          )}
        </div>
        <div>
          <span className="text-slate-600 uppercase tracking-wider">DEP</span>
          <span className="ml-1.5 font-mono text-slate-300">{fmtTime(flight.estimated_departure)}</span>
        </div>
        {flight.actual_arrival && (
          <div className="ml-auto text-slate-500 italic">{elapsedStr(flight.actual_arrival)}</div>
        )}
      </div>

      {/* Row 3: Progress */}
      {flight.ground_status !== "arriving" && (
        <div className="mb-2">
          <div className="flex justify-between text-[9px] text-slate-500 mb-1">
            <span>{flight.tasks_completed}/{flight.tasks_total} tasks</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${rs === "delayed" || rs === "critical" ? "bg-red-500" : rs === "at_risk" ? "bg-amber-500" : "bg-green-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Row 4: Bottleneck */}
      {flight.current_bottleneck && (
        <div className="text-[9px] text-amber-400/80 truncate mt-1">
          <AlertTriangle size={8} className="inline mr-1" />
          {flight.current_bottleneck}
        </div>
      )}
      {flight.ground_status === "arriving" && (
        <div className="text-[9px] text-sky-400">
          <Plane size={8} className="inline mr-1" />
          Arriving in ~{Math.abs(Math.round((new Date(flight.scheduled_arrival) - Date.now()) / 60000))} min
        </div>
      )}
    </div>
  );
}

export default function TurnaroundBoard() {
  const [flights, setFlights]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [station, setStation]       = useState("ALL");
  const [statusFilter, setStatus]   = useState("all");
  const [lastRefresh, setRefresh]   = useState(new Date());
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const q = {};
      if (station !== "ALL") q.station = station;
      if (statusFilter !== "all") q.status = statusFilter;
      const data = await groundOpsApi.listFlights(q);
      setFlights(data);
      setRefresh(new Date());
    } catch { /* silent */ } finally { setLoading(false); }
  }, [station, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const active    = flights.filter(f => !["departed", "arriving"].includes(f.ground_status));
  const on_time   = active.filter(f => f.risk_status === "on_time");
  const at_risk   = active.filter(f => f.risk_status === "at_risk");
  const delayed   = active.filter(f => ["delayed", "critical"].includes(f.risk_status));
  const arriving  = flights.filter(f => f.ground_status === "arriving");

  const displayed = statusFilter === "all" ? flights : flights.filter(f => {
    if (statusFilter === "on_time") return f.risk_status === "on_time";
    if (statusFilter === "at_risk") return f.risk_status === "at_risk";
    if (statusFilter === "delayed") return ["delayed", "critical"].includes(f.risk_status);
    return true;
  });

  return (
    <div className="p-5">
      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {[
          { label: "On Ground",  value: active.length,   color: "text-white"        },
          { label: "On Time",    value: on_time.length,  color: "text-green-400"    },
          { label: "At Risk",    value: at_risk.length,  color: "text-amber-400"    },
          { label: "Delayed",    value: delayed.length,  color: "text-red-400"      },
          { label: "Arriving",   value: arriving.length, color: "text-sky-400"      },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-[#0c1929] border border-slate-700/30 rounded-lg px-4 py-3" data-testid={`kpi-${label.toLowerCase().replace(" ","-")}`}>
            <div className={`text-2xl font-black font-mono ${color}`}>{value}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Filter size={12} className="text-slate-500" />
        <div className="flex gap-1.5">
          {STATIONS.map(s => (
            <button
              key={s}
              onClick={() => setStation(s)}
              data-testid={`filter-station-${s.toLowerCase()}`}
              className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase transition-all ${station === s ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1.5">
          {[["all", "All"], ["on_time", "On Time"], ["at_risk", "At Risk"], ["delayed", "Delayed"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setStatus(v)}
              className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${statusFilter === v ? "bg-white/10 text-white" : "text-slate-500 hover:text-slate-300"}`}
            >
              {l}
            </button>
          ))}
          <button onClick={load} className="ml-2 text-slate-500 hover:text-orange-400 transition" data-testid="refresh-btn">
            <RefreshCw size={12} />
          </button>
          <span className="text-[9px] text-slate-700 font-mono self-center">
            {lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        </div>
      </div>

      {/* ── Grid ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-60 text-slate-500 text-sm animate-pulse">Loading live ops data...</div>
      ) : displayed.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-60 text-slate-600">
          <Plane size={32} className="mb-3 opacity-30" />
          <p className="text-sm">No flights match current filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayed.map(f => (
            <FlightCard key={f.id} flight={f} onClick={id => navigate(`/turnaround/flight/${id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
